import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import moment from "moment";
import Dialog from "@material-ui/core/Dialog";
import ReactTable from "react-table";
import Button from "../Button"

import UpdateEnrollmentDetails from "./UpdateEnrollmentDetails";
import CancelEnrollment from "./CancelEnrollment";
import RerunCreditDialog from "./RerunCreditDialog";
import Tooltip from "@material-ui/core/Tooltip";
import Notifications from '@material-ui/icons/ContactMail';
import Visibility from '@material-ui/icons/Visibility';
import EnrollmentNotifications from "./EnrollmentNotifications";
import { IconButton } from "@material-ui/core";
import FileDownloadIcon from '@material-ui/icons/FileDownload';

const getDate = (value) => {
  if (!value) {
    return "";
  }
  const d = value.split("T")[0];
  return moment(d).format("YYYY-MM-DD");
};

const wildcardFilter = (filter, row) => row[filter.id].toLowerCase().indexOf(filter.value.toLowerCase()) !== -1;

const dataColumns = [
  {
    Header: "ID",
    accessor: "id",
  },
  {
    Header: "Type",
    accessor: "enrollment_type",
  },
  {
    Header: "Email",
    accessor: "email",
  },
  {
    Header: "Phone",
    accessor: "phone",
  },
  {
    Header: "Name",
    id: "first_name",
    filterMethod: wildcardFilter,
    accessor: (row) => row.first_name + " " + row.last_name,
  },
  {
    Header: "Meter Identifier",
    accessor: "meter_identifier",
    filterMethod: wildcardFilter,
  },
  {
    Header: "Start Date",
    accessor: "requested_start_date",
    filterMethod: wildcardFilter,
  },
  {
    Header: "Verification",
    accessor: "verification_status",
    filterMethod: wildcardFilter,
  },
  {
    Header: "Deposit Paid",
    id: "deposit_paid",
    accessor: (row) => {
      if (row?.deposit_amount === 0) return "-";
      return row?.deposit_paid ? "Paid" : "Not Paid"
    },
    filterMethod: wildcardFilter,
  },
  {
    Header: "Status",
    accessor: "enrollment_status",
    filterMethod: wildcardFilter,
  },
  {
    Header: "Mode",
    accessor: "enroll_mode",
    filterMethod: wildcardFilter,
  },
  {
    Header: "Created",
    id: "created_date",
    accessor: (row) => getDate(row.created_date),
    filterMethod: wildcardFilter,
  },
  {
    Header: "Completed",
    id: "completed_date",
    accessor: (row) => getDate(row.completed_date),
    filterMethod: wildcardFilter,
  },
  {
    Header: "Service",
    accessor: "service_type",
    filterMethod: wildcardFilter,
  },
  {
    Header: "State",
    id: "state",
    filterMethod: wildcardFilter,
    accessor: (row) => row?.address?.state || '-',
  },
  {
    Header: "Zip Code",
    id: "postal_code",
    filterMethod: wildcardFilter,
    accessor: (row) => row?.address?.postal_code || '-',
  },
  {
    Header: "Premise",
    accessor: "premise_type",
    filterMethod: wildcardFilter,
  },
  {
    Header: "Company",
    id: "company",
    accessor: (row) => row.premise_type && row.premise_type.toLowerCase() === 'commercial' ? row.company : '',
    filterMethod: wildcardFilter,
  },
  {
    Header: "Sales Channel",
    id: "sales_channel_name",
    filterMethod: wildcardFilter,
    accessor: (row) => row?.sales_channel_name || '-',
  },
];

export const DownloadCSVButton = ({ enrollments }) => {
  const downloadCSV = (data) => {
    const header = dataColumns.map((x) => x.Header).join(",");
    const values = data.map((x) => dataColumns.map((y) => (y.accessor instanceof Function ? y.accessor(x) : x[y.accessor])).join(","));
    const csv = [header, ...values].join("\n");
    const csvContent = `data:text/csv;charset=utf-8,${csv}`;
    const encodedURI = encodeURI(csvContent);
    window.open(encodedURI);
  };

  return (
    <IconButton
      aria-label="Export Report"
      size="medium"
      title="Export Report"
      color="primary"
      onClick={() => downloadCSV(enrollments)}
    >
      <FileDownloadIcon fontSize="large" />
    </IconButton>
  );
}

DownloadCSVButton.propTypes = {
  enrollments: PropTypes.array,
};


const EnrollmentTable = ({
  searchCriteria,
  enrollments,
  fetchEnrollments,
  fetchCancelEnrollments,
  resubmitEnrollment,
  fetchUpdateEnrollmentName,
  rerunCredit,
  fetchAddNote,
  fetchSendVerification,
  isHold,
  adminUser,
}) => {
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rerunDialogOpen, setRerunDialogOpen] = useState(false);
  const [notficationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [cancelStatus, setCancelStatus] = useState("canceled");
  const [selectedEnrollment, setSelectedEnrollment] = useState({});

  const columns = [...dataColumns, {
    Header: "",
    resizable: false,
    filterable: false,
    width: 150,
    Cell: (row) => (
      <div>
        <Tooltip placement="top" title="View/Edit Data">
          <Button
            style={{ marginRight: '5px' }}
            onClick={async () => {
              await setSelectedEnrollment(row.original);
              await setUpdateDialogOpen(true);
            }}
            variant="contained"
            color="secondary"
            size="small"
          >
            <Visibility />
          </Button>
        </Tooltip>
        <Tooltip placement="top" title="View Notifications">
          <Button
            style={{ marginRight: '5px' }}
            onClick={async () => {
              await setSelectedEnrollment(row.original);
              await setNotificationDialogOpen(true);
            }}
            variant="contained"
            color="secondary"
            size="small"
          >
            <Notifications />
          </Button>
        </Tooltip>
      </div>
    ),
  }];

  useEffect(() => {
    if (selectedEnrollment.id) {
      const en = enrollments.find(e => e.id === selectedEnrollment.id);

      if (en) {
        setSelectedEnrollment(en);
      } else {
        handleCloseUpdateDialog();
        setSelectedEnrollment({});
      }
    }
  }, [enrollments]);


  const handleCloseUpdateDialog = () => setUpdateDialogOpen(false);
  const handleCloseCancelDialog = () => setCancelDialogOpen(false);
  const handleCloseRerunDialog = () => setRerunDialogOpen(false);
  const handleCloseNotficationDialog = () => setNotificationDialogOpen(false);
  const handleResubmit = (
    id,
    isWaivedDeposit,
    isCompleted,
    isVerified,
    isIDVerified,
    isReleased,
    releaseNote,
    isAgentCompleted,
  ) => {
    resubmitEnrollment(
      searchCriteria,
      id,
      isWaivedDeposit,
      isCompleted,
      isVerified,
      isIDVerified,
      isReleased,
      releaseNote,
      isAgentCompleted,
      isHold,
    );
  };

  const handleCancel = (id, notes, status) => {
    fetchCancelEnrollments({
      enrollment_id: id,
      searchCriteria: searchCriteria,
      notes,
      status,
    }, isHold);
  }

  const handleEdit = obj => {
    fetchUpdateEnrollmentName({
      update_enrollment: obj,
      searchCriteria: searchCriteria,
    }, isHold);
  }

  const handleRerun = (details, socialNumber) => {
    rerunCredit({
      details,
      socialNumber,
      searchCriteria: searchCriteria,
    }, isHold);
  }

  const handleAddNote = ({ id, note }) => {
    fetchAddNote({
      enrollment_id: id,
      note: note,
      searchCriteria: searchCriteria,
    });
  }

  const handleVerification = obj => {
    fetchSendVerification({
      enrollment: obj,
      searchCriteria: searchCriteria,
    }, isHold);
  }

  return (
    <div>
      <ReactTable
        data={enrollments}
        columns={columns}
        style={{ textAlign: 'center' }}
        className="-striped -highlight"
        defaultPageSize={10}
        defaultSortOrder="created_date"
        defaultSortDesc
        filterable
        defaultSorted={[
          {
            id: "created_date",
            desc: true,
          },
        ]}
      />
      <Dialog
        maxWidth={"lg"}
        onClose={handleCloseUpdateDialog}
        aria-labelledby="customized-dialog-title"
        open={updateDialogOpen}
      >
        <UpdateEnrollmentDetails
          details={selectedEnrollment}
          onStatusChange={() => { }}
          closePopup={handleCloseUpdateDialog}
          onResubmit={handleResubmit}
          onEdit={handleEdit}
          onAddNote={handleAddNote}
          onVerification={handleVerification}
          adminUser={adminUser}
          setCancelDialogOpen={setCancelDialogOpen}
          setCancelStatus={setCancelStatus}
          setRerunDialogOpen={rerunCredit ? setRerunDialogOpen : undefined}
        />
      </Dialog>
      <Dialog
        onClose={handleCloseCancelDialog}
        aria-labelledby="customized-dialog-title"
        open={cancelDialogOpen}
      >
        <CancelEnrollment
          details={selectedEnrollment}
          onCancel={handleCancel}
          handleCloseCancelDialog={handleCloseCancelDialog}
          cancelStatus={cancelStatus}
        />
      </Dialog>
      {rerunCredit && (
        <Dialog
          onClose={handleCloseRerunDialog}
          aria-labelledby="customized-dialog-title"
          open={rerunDialogOpen}
        >
          <RerunCreditDialog
            details={selectedEnrollment}
            onRerun={handleRerun}
            handleCloseRerunDialog={handleCloseRerunDialog}
          />
        </Dialog>
      )}
      <Dialog
        maxWidth="md"
        onClose={handleCloseNotficationDialog}
        aria-labelledby="customized-dialog-title"
        open={notficationDialogOpen}
      >
        <EnrollmentNotifications
          details={selectedEnrollment}
          closePopup={handleCloseNotficationDialog}
        />
      </Dialog>

    </div>
  );
};

EnrollmentTable.propTypes = {
  enrollments: PropTypes.array,
};

export default EnrollmentTable;
